import React from 'react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  onPageNext, 
  onPagePrev,
  disabled = false,
  maxVisiblePages = 5
}) => {
  if (totalPages <= 1) return null;

  // Вычисляем диапазон отображаемых страниц
  const getPageNumbers = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);
    
    // Корректируем, если мы близко к началу
    if (currentPage <= halfVisible) {
      endPage = Math.min(totalPages, maxVisiblePages);
    }
    
    // Корректируем, если мы близко к концу
    if (currentPage + halfVisible >= totalPages) {
      startPage = Math.max(1, totalPages - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();
  const showStartEllipsis = pageNumbers[0] > 2;
  const showEndEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages - 1;

  const handlePageClick = (page) => {
    if (!disabled && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrev = () => {
    if (!disabled && currentPage > 1) {
      onPagePrev();
    }
  };

  const handleNext = () => {
    if (!disabled && currentPage < totalPages) {
      onPageNext();
    }
  };

  return (
    <div className="flex items-center justify-center space-x-2">
      {/* Кнопка "Первая" */}
      <button
        onClick={() => handlePageClick(1)}
        disabled={disabled || currentPage === 1}
        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
          disabled || currentPage === 1
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
        }`}
        title="Первая страница"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

      {/* Кнопка "Предыдущая" */}
      <button
        onClick={handlePrev}
        disabled={disabled || currentPage === 1}
        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
          disabled || currentPage === 1
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
        }`}
        title="Предыдущая страница"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Номера страниц */}
      <div className="hidden sm:flex items-center space-x-1">
        {/* Первая страница */}
        {pageNumbers[0] > 1 && (
          <>
            <button
              onClick={() => handlePageClick(1)}
              disabled={disabled}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                disabled
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                  : currentPage === 1
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              1
            </button>
            {showStartEllipsis && (
              <span className="px-2 text-gray-500">...</span>
            )}
          </>
        )}

        {/* Основные номера страниц */}
        {pageNumbers.map((page) => (
          <button
            key={page}
            onClick={() => handlePageClick(page)}
            disabled={disabled}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
              disabled
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : currentPage === page
                ? 'border-blue-500 bg-blue-50 text-blue-600'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            {page}
          </button>
        ))}

        {/* Последняя страница */}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <>
            {showEndEllipsis && (
              <span className="px-2 text-gray-500">...</span>
            )}
            <button
              onClick={() => handlePageClick(totalPages)}
              disabled={disabled}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                disabled
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                  : currentPage === totalPages
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>

      {/* Мобильный вариант - текущая страница из всего */}
      <div className="sm:hidden px-3 py-2 text-sm text-gray-700">
        {currentPage} / {totalPages}
      </div>

      {/* Кнопка "Следующая" */}
      <button
        onClick={handleNext}
        disabled={disabled || currentPage === totalPages}
        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
          disabled || currentPage === totalPages
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
        }`}
        title="Следующая страница"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Кнопка "Последняя" */}
      <button
        onClick={() => handlePageClick(totalPages)}
        disabled={disabled || currentPage === totalPages}
        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
          disabled || currentPage === totalPages
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400'
        }`}
        title="Последняя страница"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      </button>

      {/* Информация о странице */}
      <div className="hidden md:block text-sm text-gray-600 ml-4">
        Страница {currentPage} из {totalPages}
      </div>
    </div>
  );
};

export default Pagination;
